import { startSlider } from './config.js';

const ACTIVE_SLIDE_CLASS = 'platform-control-section__slide--active';
const ACTIVE_STEP_CLASS = 'platform-control-section__step--active';
const MOVING_CLASS = 'platform-control-section__slide--moving';
const HOLD = 10000;
const TRAVEL = 'cubic-bezier(0.85, 0, 0.15, 1)';
const SETTLE = 'cubic-bezier(0.45, 0, 0.55, 1)';
const CARD_SCALES = [1.05, 1, 1.155, 1.155, 1.2375];
const CONFIGS = [
    { rings: '3081:10290', cards: [['3081:10298', -1150, -510, .16], ['3081:10382', 620, -410, .24], ['3081:10343', 0, 500, .32]], clusters: ['3081:10303', '3081:10311', '3081:10315', '3081:10326', '3081:10329', '3081:10337', '3081:10365', '3081:10368', '3081:10376'] },
    { rings: '3081:10752', cards: ['3081:10795', '3081:10802', '3081:10812', '3081:10823', '3081:10828', '3081:10831', '3081:10832', '3081:10843'].map((id, index) => [id, 0, 80, .12 + index * .04]), clusters: [] },
    { rings: '3081:10535', cards: [['3081:10543', 0, 500, .08], ['3081:10633', 0, 540, .16], ['3081:10664', -180, 0, 1.7], ['3081:10684', -180, 0, 1.78], ['3081:10707', -180, 0, 1.86]], clusters: ['3081:10681', '3081:10643', '3081:10657'] },
    { rings: '3081:10401', cards: [['3081:10499', -1120, -410, .16], ['3081:10428', 630, -350, .24], ['3081:10406', 0, 450, .32]], clusters: [] },
    { rings: '3081:10712', cards: [['3081:10744', -50, 0, .16], ['3081:10746', -50, 0, .16], ['3081:10717', -50, 0, .16], ['3081:10742', 50, 0, .16]], clusters: ['3081:10734'] },
];

const node = (root, id) => root?.querySelector('[data-node-id="' + id + '"]');

function wrap(element) {
    const wrapper = document.createElement('div');
    wrapper.className = 'platform-control-section__motion-wrap';
    let parent = element.parentElement;
    while (parent && getComputedStyle(parent).display === 'contents') {
        parent = parent.parentElement;
    }
    const size = getComputedStyle(parent);
    wrapper.style.width = size.width;
    wrapper.style.height = size.height;
    element.before(wrapper);
    wrapper.append(element);
    return wrapper;
}

function measure(elements, composition) {
    const base = composition.getBoundingClientRect();
    const scale = base.width / composition.offsetWidth || 1;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    elements.forEach((root) => [root, ...root.querySelectorAll('div')].forEach((element) => {
        const rect = element.getBoundingClientRect();
        const style = getComputedStyle(element);
        const surface = parseFloat(style.borderRadius) > 0 || parseFloat(style.borderTopWidth) > 0;
        if (rect.width / scale < 30 || rect.height / scale < 15 || !(element === root || surface)) {
            return;
        }
        minX = Math.min(minX, (rect.left - base.left) / scale);
        minY = Math.min(minY, (rect.top - base.top) / scale);
        maxX = Math.max(maxX, (rect.right - base.left) / scale);
        maxY = Math.max(maxY, (rect.bottom - base.top) / scale);
    }));
    return { minX, minY, maxX, maxY };
}

function prepareCard(card, config) {
    const units = new Set();
    card.shells = [];
    card.element.style.borderColor = 'transparent';
    // Fade the glass surface itself so ancestors do not isolate backdrop blur.
    [card.element, ...card.element.querySelectorAll('div')].forEach((element) => {
        const style = getComputedStyle(element);
        const blur = style.backdropFilter || style.webkitBackdropFilter;
        if (!blur || blur === 'none') {
            return;
        }
        const surface = document.createElement('div');
        surface.className = 'platform-control-section__glass-surface';
        surface.dataset.glassSurface = 'true';
        Object.assign(surface.style, {
            position: 'absolute', inset: '-' + style.borderTopWidth,
            borderRadius: style.borderRadius, background: style.background,
            boxShadow: style.boxShadow, backdropFilter: blur, webkitBackdropFilter: blur,
            pointerEvents: 'none', zIndex: '-1',
        });
        Object.assign(element.style, {
            borderColor: 'transparent', background: 'transparent', boxShadow: 'none',
            backdropFilter: 'none', webkitBackdropFilter: 'none', isolation: 'auto', zIndex: '0',
        });
        element.prepend(surface);
        card.shells.push(surface);
    });
    card.element.querySelectorAll('div').forEach((element) => {
        const style = getComputedStyle(element);
        if (!element.dataset.glassSurface && style.backgroundColor !== 'rgba(0, 0, 0, 0)' && parseFloat(style.borderRadius) === 0) {
            card.shells.push(element);
        }
    });
    if (!card.shells.length) {
        card.shells.push(card.element);
    }
    config.clusters.forEach((id) => {
        let element = node(card.wrapper, id);
        if (!element) {
            return;
        }
        if (getComputedStyle(element).display === 'contents') {
            element = wrap(element);
        }
        units.add(element);
    });
    card.wrapper.querySelectorAll('p,img,object').forEach((element) => units.add(element));
    card.wrapper.querySelectorAll('div').forEach((element) => {
        if (element === card.element || element === card.wrapper || element.classList.contains('platform-control-section__motion-wrap') || element.dataset.glassSurface || element.dataset.divider || element.dataset.name === 'logo_slot') {
            return;
        }
        const style = getComputedStyle(element);
        const width = parseFloat(style.width);
        const height = parseFloat(style.height);
        const visual = element.querySelector('p,img,object') || style.backgroundColor !== 'rgba(0, 0, 0, 0)';
        if (width > 0 && width <= 520 && height > 0 && height <= 105 && parseFloat(style.borderRadius) > 0 && visual) {
            units.add(element);
        }
    });
    const content = Array.from(units).filter((element) => !element.querySelector('[data-glass-surface]'));
    card.leaves = content.filter((element) => !content.some((other) => other !== element && other.contains(element)));
    card.leaves.forEach((element) => { element.dataset.finalOpacity = getComputedStyle(element).opacity; });
    card.lines = [];
    card.wrapper.querySelectorAll('div').forEach((element) => {
        const style = getComputedStyle(element);
        if (parseFloat(style.height) < 3 && parseFloat(style.width) > 15 && style.backgroundColor !== 'rgba(0, 0, 0, 0)' && !card.leaves.some((unit) => unit.contains(element))) {
            card.lines.push(element);
        }
        if (element.dataset.divider && parseFloat(style.borderBottomWidth) > 0 && element.children.length) {
            const color = style.borderBottomColor;
            element.style.borderBottomColor = 'transparent';
            if (element.dataset.nodeId === '3081:10408') {
                return;
            }
            const line = document.createElement('span');
            Object.assign(line.style, {
                position: 'absolute', insetInline: '0', insetBlockEnd: '0',
                height: style.borderBottomWidth, background: color,
                transformOrigin: '0 50%', pointerEvents: 'none',
            });
            element.append(line);
            card.lines.push(line);
        }
    });
}

function prepareModel(slide) {
    const composition = slide.querySelector('.platform-control-artwork');
    const artworkIndex = Number(slide.dataset.platformControlArtworkIndex);
    const config = composition ? CONFIGS[artworkIndex] : null;
    const model = {
        copy: slide.querySelector('.platform-control-section__card-content'),
        media: slide.querySelector('.platform-control-section__fallback-artwork'),
        composition, artworkIndex, cards: [], centre: null, glow: null,
    };
    if (!config) {
        return model;
    }
    if (artworkIndex === 4) {
        composition.append(node(composition, '3081:10717'));
    }
    if (artworkIndex === 2) {
        node(composition, '3081:10663').style.overflow = 'visible';
    }
    if (artworkIndex === 3) {
        const grid = node(composition, '3081:10410');
        const positions = [1.63376, 96.199, 191.302, 286.404, 380.545, 477.137, 570];
        Object.assign(grid.style, { display: 'block', width: '632px', height: '436px' });
        Array.from(grid.children).forEach((column, index) => Object.assign(column.style, {
            position: 'absolute', insetInlineStart: (32.04 + positions[index]) + 'px',
            insetBlockStart: '0', width: '80px', transform: 'translateX(-50%)',
        }));
    }
    model.glow = wrap(node(composition, config.rings));
    model.glow.classList.add('platform-control-section__glow');
    if (artworkIndex === 1) {
        composition.querySelectorAll('[data-name="logo_slot"]').forEach((slot) => {
            const width = parseFloat(getComputedStyle(slot).width);
            if (width < 150) {
                slot.style.transform = 'scale(' + 152.579 / width + ')';
                slot.style.transformOrigin = '0 0';
            }
        });
    }
    composition.querySelectorAll('div').forEach((element) => {
        const style = getComputedStyle(element);
        if (style.filter.startsWith('drop-shadow')) {
            const shadow = style.filter.match(/drop-shadow\((rgba?\([^)]*\))\s+([^)]*)\)/);
            element.style.filter = 'none';
            if (element.firstElementChild && shadow) {
                element.firstElementChild.style.boxShadow = shadow[2] + ' ' + shadow[1];
            }
        }
        const blur = (style.backdropFilter || style.webkitBackdropFilter || '').match(/blur\(([\d.]+)px\)/);
        if (blur) {
            const value = 'blur(' + Number(blur[1]) * 3 + 'px)';
            element.style.backdropFilter = value;
            element.style.webkitBackdropFilter = value;
        } else if (element.dataset.name === 'logo-plaque') {
            element.style.backdropFilter = 'blur(18px)';
            element.style.webkitBackdropFilter = 'blur(18px)';
        }
        if (style.borderTopColor === 'rgb(255, 255, 255)' && parseFloat(style.borderRadius) > 0 && parseFloat(style.borderTopWidth) > 0 && !element.dataset.divider) {
            element.style.borderColor = 'transparent';
        }
    });
    config.cards.forEach(([id, dx, dy, at]) => {
        const element = node(composition, id);
        if (element) {
            model.cards.push({
                element, wrapper: wrap(element), dx, dy, at, x: dx, y: dy,
                dialogue: artworkIndex === 4, horizontal: artworkIndex === 2 && at >= 1.7,
            });
        }
    });
    if (artworkIndex === 4) {
        model.cards[1].wrapper.append(node(composition, '3081:10749'));
        model.cards[2].wrapper.append(node(composition, '3081:10750'));
        model.cards[3].wrapper.append(node(composition, '3081:10748'));
    }
    model.bounds = measure(model.cards.flatMap((card) => Array.from(card.wrapper.children)), composition);
    model.cards.forEach((card) => prepareCard(card, config));
    return model;
}

function fit(model, width, stacked) {
    if (!model.composition) {
        model.artHeight = stacked ? 360 : 584;
        return;
    }
    const b = model.bounds;
    const vertical = Math.max(752 - b.minY, b.maxY - 752);
    const horizontal = Math.max(1731 - b.minX, b.maxX - 1731);
    model.circleScale = stacked ? Math.min((width - 48) / (horizontal * 2), .39) : Math.min(444 / (vertical * 2), .39);
    let scale = model.circleScale * CARD_SCALES[model.artworkIndex];
    if (stacked) {
        scale = Math.min(scale, (width - 48) / (model.artworkIndex >= 2 ? b.maxX - b.minX : 2 * horizontal));
    }
    model.compositionScale = scale;
    model.artHeight = stacked ? 140 + 2 * vertical * scale : 584;
    model.offsetX = model.artworkIndex >= 2 ? -(b.minX + b.maxX - 3462) * scale / 2 : 0;
    model.offsetY = model.artworkIndex >= 2 ? -(b.minY + b.maxY - 1504) * scale / 2 : 0;
    model.cards.forEach((card) => { card.x = card.dx / scale; card.y = card.dy / scale; });
}

function settle(model) {
    Object.assign(model.copy.style, { opacity: '1', transform: 'none' });
    if (model.glow) {
        Object.assign(model.glow.style, { opacity: '1', transform: 'scale(1)' });
    }
    if (model.centre) {
        model.centre.style.opacity = '1';
    }
    if (model.media) {
        Object.assign(model.media.style, { opacity: '1', transform: 'none' });
    }
    model.cards.forEach((card) => {
        card.wrapper.style.transform = 'none';
        card.shells.forEach((element) => { element.style.opacity = '1'; });
        card.leaves.forEach((element) => Object.assign(element.style, { opacity: element.dataset.finalOpacity, transform: 'none' }));
        card.lines.forEach((element) => { element.style.transform = 'scaleX(1)'; });
    });
}

const initPlatformControlSlider = async (slider) => {
    const section = slider.closest('.platform-control-section');
    const slides = Array.from(slider.querySelectorAll('[data-platform-control-slide]'));
    if (!section || !slides.length || slider.dataset.platformControlInitialized) {
        return;
    }
    slider.dataset.platformControlInitialized = 'true';
    const tabs = Array.from(section.querySelectorAll('[data-platform-control-step-button]'));
    const steps = section.querySelector('[data-platform-control-steps]');
    const stepsWrapper = section.querySelector('[data-platform-control-steps-wrapper]');
    const play = section.querySelector('[data-platform-control-play]');
    const counter = section.querySelector('[data-platform-control-counter]');
    const progress = section.querySelector('[data-platform-control-progress]');
    const bar = section.querySelector('[data-platform-control-progress-bar]');
    const status = section.querySelector('[data-platform-control-status]');
    const shared = section.querySelector('[data-platform-control-shared]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stackedMedia = window.matchMedia('(max-width: 1024px)');
    const initialIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains(ACTIVE_SLIDE_CLASS)));
    let index = initialIndex;
    let phase = 'hold';
    let autoplay = false;
    let paused = false;
    let resumable = false;
    let started = false;
    let visible = false;
    let holdTime = 0;
    let lastTime = 0;
    let timeline = null;
    let leaving = null;
    let generation = 0;
    let frame = 0;
    let swiper = null;
    let suppressSlideChange = false;
    let stepsSwiper = null;
    let suppressStepsChange = false;
    let stepsFrame = 0;
    let stepsWidth = 0;

    // Other sections' images and fonts do not affect these artwork bounds.
    await Promise.all([
        document.fonts?.ready,
        ...Array.from(slider.querySelectorAll('img')).map((image) => image.decode?.().catch(() => {})),
    ]);
    const models = slides.map(prepareModel);
    const circleModel = models.find((model) => model.artworkIndex === 0 && model.glow) || models.find((model) => model.glow && model.artworkIndex !== 1);
    const glow = circleModel?.glow;
    if (shared && glow) {
        shared.append(glow);
        models.forEach((model) => {
            if (!model.glow) {
                return;
            }
            if (model.artworkIndex === 1) {
                model.centre = model.glow;
                model.centre.classList.remove('platform-control-section__glow');
                model.centre.classList.add('platform-control-section__integration-centre');
                model.centre.style.width = '2633px';
                model.centre.style.height = '1503px';
                model.centre.style.opacity = '0';
            } else if (model.glow !== glow) {
                model.glow.remove();
            }
            model.glow = glow;
        });
    }

    function resize() {
        const width = slider.clientWidth;
        if (!width) {
            return;
        }
        const stacked = stackedMedia.matches;
        const rtl = getComputedStyle(section).direction === 'rtl';
        section.style.setProperty('--platform-control-scale', width / 1719);
        models.forEach((model) => fit(model, width, stacked));
        const artHeight = Math.max(...models.map((model) => model.artHeight));
        const artTop = stacked ? Math.max(230, models[index].copy.offsetHeight + (width <= 600 ? 50 : 70)) : 0;
        section.style.setProperty('--platform-control-art-top', artTop + 'px');
        section.style.setProperty('--platform-control-art-height', artHeight + 'px');
        slider.style.height = (stacked ? artTop + artHeight : 584 * width / 1719) + 'px';
        models.forEach((model) => {
            if (!model.composition) {
                return;
            }
            const scale = model.compositionScale;
            const cx = (stacked ? width / 2 : rtl ? 590 : 1129) + model.offsetX;
            const cy = (stacked ? artHeight / 2 : 292) + model.offsetY;
            model.composition.style.transform = 'translate(' + (cx - 1731 * scale) + 'px,' + (cy - 752 * scale) + 'px) scale(' + scale + ')';
            if (model.artworkIndex === 2) {
                const stroke = 1 / (scale * (stacked ? 1 : width / 1719));
                ['3081:10560', '3081:10562', '3081:10565'].forEach((id) => {
                    const pill = node(model.composition, id);
                    pill.style.borderColor = 'transparent';
                    pill.style.boxShadow = 'inset 0 0 0 ' + stroke + 'px var(--pc-art-color-16)';
                });
            }
        });
        if (shared && glow) {
            const scale = circleModel.circleScale;
            const cx = stacked ? width / 2 : rtl ? 590 : 1129;
            const cy = stacked ? artHeight / 2 : 292;
            shared.style.transform = 'translate(' + (cx - 1731 * scale) + 'px,' + (cy - 752 * scale) + 'px) scale(' + scale + ')';
        }
    }


    function measureStepsWidth() {
        const measurement = document.createElement('div');
        measurement.setAttribute('aria-hidden', 'true');
        Object.assign(measurement.style, {
            position: 'absolute',
            insetInlineStart: '0',
            insetBlockStart: '0',
            display: 'flex',
            width: 'max-content',
            visibility: 'hidden',
            pointerEvents: 'none',
        });
        const clones = tabs.map((tab) => {
            const clone = tab.cloneNode(true);
            clone.removeAttribute('style');
            clone.removeAttribute('id');
            clone.removeAttribute('data-platform-control-step-button');
            clone.removeAttribute('aria-controls');
            clone.classList.remove('swiper-slide', 'swiper-slide-active', 'swiper-slide-prev', 'swiper-slide-next');
            Object.assign(clone.style, {
                width: 'max-content',
                maxWidth: 'none',
                minWidth: '0',
                flex: '0 0 auto',
                whiteSpace: 'nowrap',
                transition: 'none',
            });
            measurement.append(clone);
            return clone;
        });
        section.append(measurement);
        const gap = parseFloat(getComputedStyle(steps).getPropertyValue('--platform-control-step-gap')) || 16;
        const width = clones.reduce((total, clone) => total + clone.getBoundingClientRect().width, 0) + gap * Math.max(0, clones.length - 1);
        measurement.remove();
        return width;
    }

    function destroyStepsSlider() {
        if (stepsSwiper) {
            suppressStepsChange = true;
            stepsSwiper.destroy(true, true);
            stepsSwiper = null;
            suppressStepsChange = false;
        }
        steps.classList.remove('platform-control-section__steps--slider', 'swiper');
        stepsWrapper.classList.remove('swiper-wrapper');
        tabs.forEach((tab) => tab.classList.remove('swiper-slide', 'swiper-slide-active', 'swiper-slide-prev', 'swiper-slide-next'));
        stepsWidth = 0;
    }

    function layoutSteps() {
        stepsFrame = 0;
        if (!steps || !stepsWrapper) {
            return;
        }
        const width = steps.clientWidth;
        if (!width) {
            return;
        }
        const needsSlider = stackedMedia.matches && measureStepsWidth() > width + 1;
        if (!needsSlider) {
            destroyStepsSlider();
            return;
        }
        suppressStepsChange = true;
        if (!stepsSwiper) {
            steps.classList.add('platform-control-section__steps--slider', 'swiper');
            stepsWrapper.classList.add('swiper-wrapper');
            tabs.forEach((tab) => tab.classList.add('swiper-slide'));
            stepsSwiper = startSlider(steps, {
                initialSlide: index,
                centeredSlides: true,
                slidesPerView: 'auto',
                spaceBetween: 16,
                speed: reduced.matches ? 0 : 350,
                rewind: true,
                watchOverflow: false,
                grabCursor: true,
                a11y: { enabled: false },
                on: {
                    slideChange(currentSwiper) {
                        if (!suppressStepsChange) {
                            selectManual(currentSwiper.activeIndex);
                        }
                    },
                },
            });
            stepsWidth = width;
        } else if (Math.abs(width - stepsWidth) > .5) {
            stepsSwiper.update();
            stepsWidth = width;
        }
        if (stepsSwiper && stepsSwiper.activeIndex !== index) {
            stepsSwiper.slideTo(index, reduced.matches ? 0 : 350);
        }
        suppressStepsChange = false;
    }

    function scheduleStepsLayout() {
        if (steps && stepsWrapper && !stepsFrame) {
            stepsFrame = requestAnimationFrame(layoutSteps);
        }
    }

    function updatePlay() {
        const running = autoplay && !paused;
        play?.setAttribute('aria-pressed', String(running));
        play?.setAttribute('aria-label', running ? play.dataset.pauseLabel : resumable ? play.dataset.resumeLabel : play.dataset.playLabel);
        play?.classList.toggle('platform-control-section__play--playing', running);
    }

    function paintProgress() {
        const fraction = autoplay ? (index + (phase === 'hold' ? Math.min(holdTime / HOLD, 1) : 0)) / slides.length : (index + 1) / slides.length;
        bar?.style.setProperty('--platform-control-progress', fraction);
        bar?.classList.toggle('platform-control-section__progress-bar--live', autoplay);
        progress?.setAttribute('aria-valuenow', String(index + 1));
    }

    function updateUI() {
        slides.forEach((slide, slideIndex) => {
            const active = slideIndex === index;
            slide.classList.toggle(ACTIVE_SLIDE_CLASS, active);
            slide.setAttribute('aria-hidden', String(!active));
            slide.inert = !active;
            slide.style.zIndex = active ? '2' : '1';
        });
        tabs.forEach((tab, tabIndex) => {
            const active = tabIndex === index;
            tab.classList.toggle(ACTIVE_STEP_CLASS, active);
            tab.setAttribute('aria-selected', String(active));
            tab.tabIndex = active ? 0 : -1;
        });
        if (counter) {
            counter.textContent = String(index + 1) + '/' + String(slides.length);
        }
        if (status) {
            status.textContent = tabs[index]?.textContent.trim() || '';
        }
        updatePlay();
        paintProgress();
        resize();
        scheduleStepsLayout();
    }

    function cleanup() {
        leaving = null;
        slides.forEach((slide) => {
            slide.hidden = false;
            slide.classList.remove(MOVING_CLASS);
        });
    }

    function pauseTimeline() {
        timeline?.animations.forEach((animation) => {
            if (animation.playState === 'running') {
                animation.pause();
            }
        });
    }

    function resumeTimeline() {
        if (!visible || paused || document.hidden) {
            return;
        }
        requestTick();
        timeline?.animations.forEach((animation) => {
            if (animation.playState !== 'paused') {
                return;
            }
            const endTime = animation.effect.getComputedTiming().endTime;
            if (animation.currentTime < endTime) {
                animation.play();
            }
        });
    }

    function stopTimeline() {
        if (!timeline) {
            return;
        }
        const elements = new Set(timeline.animations.map((animation) => animation.effect.target));
        const snapshots = Array.from(elements).map((element) => {
            const style = getComputedStyle(element);
            return [element, style.transform, style.opacity];
        });
        timeline.animations.forEach((animation) => animation.cancel());
        timeline = null;
        snapshots.forEach(([element, transform, opacity]) => Object.assign(element.style, { transform, opacity }));
    }

    function segment(sequence, element, values, at, duration, easing = TRAVEL) {
        if (element) {
            sequence.push({ element, values, at: at * 1000, duration: duration * 1000, easing });
        }
    }

    function entrance(model, firstAppearance) {
        const sequence = [];
        const copyX = getComputedStyle(section).direction === 'rtl' ? 450 : -450;
        Object.assign(model.copy.style, { opacity: '0', transform: 'translateX(' + copyX + 'px)' });
        segment(sequence, model.copy, { transform: ['translateX(' + copyX + 'px)', 'translateX(0px)'], opacity: [0, 1] }, 0, .8, SETTLE);
        if (model.media) {
            Object.assign(model.media.style, { opacity: '0', transform: 'translateY(120px)' });
            segment(sequence, model.media, { opacity: [0, 1], transform: ['translateY(120px)', 'translateY(0px)'] }, .16, 1.15);
        }
        model.cards.forEach((card) => {
            card.wrapper.style.transform = 'translate(' + card.x + 'px,' + card.y + 'px)';
            card.shells.forEach((element) => { element.style.opacity = '0'; });
            card.leaves.forEach((element) => Object.assign(element.style, { opacity: '0', transform: 'translateY(12px)' }));
            card.lines.forEach((element) => { element.style.transform = 'scaleX(0)'; });
            const travel = card.dialogue ? .75 : 1.15;
            const arrival = card.horizontal ? 'translate(0px,0px)' : 'translate(0px,-5px)';
            segment(sequence, card.wrapper, { transform: [card.wrapper.style.transform, arrival] }, card.at, travel, card.dialogue ? SETTLE : TRAVEL);
            card.shells.forEach((element) => segment(sequence, element, { opacity: [0, 1] }, card.at, travel, card.dialogue ? SETTLE : TRAVEL));
            if (!card.horizontal) {
                segment(sequence, card.wrapper, { transform: ['translate(0px,-5px)', 'translate(0px,0px)'] }, card.at + travel, card.dialogue ? .2 : .25, SETTLE);
            }
            const contentAt = card.at + (card.dialogue ? .3 : .72);
            card.leaves.forEach((element, leafIndex) => {
                const delay = Math.min(leafIndex * .04, card.dialogue ? .18 : .3);
                const y = element.tagName === 'IMG' ? -10 : 12;
                segment(sequence, element, { transform: ['translateY(' + y + 'px)', 'translateY(0px)'], opacity: [0, Number(element.dataset.finalOpacity)] }, contentAt + delay, .8);
            });
            card.lines.forEach((element, lineIndex) => segment(sequence, element, { transform: ['scaleX(0)', 'scaleX(1)'] }, contentAt + .08 + lineIndex * .025, .65));
        });
        if (model.glow) {
            if (firstAppearance) {
                Object.assign(model.glow.style, { opacity: '0', transform: 'scale(.8)' });
                segment(sequence, model.glow, { opacity: [0, 1], transform: ['scale(.8)', 'scale(1)'] }, 0, 1.2, SETTLE);
            } else {
                const style = getComputedStyle(model.glow);
                segment(sequence, model.glow, { opacity: [parseFloat(style.opacity), .55], transform: [style.transform, 'scale(.8)'] }, 0, .45, SETTLE);
                segment(sequence, model.glow, { opacity: [.55, 1], transform: ['scale(.8)', 'scale(1)'] }, .45, .75, SETTLE);
            }
        }
        if (model.centre) {
            model.centre.style.opacity = '0';
            segment(sequence, model.centre, { opacity: [0, 1] }, .3, .65, SETTLE);
        }
        return sequence;
    }

    function exit(model) {
        const sequence = [];
        const copyX = getComputedStyle(section).direction === 'rtl' ? 450 : -450;
        const style = getComputedStyle(model.copy);
        segment(sequence, model.copy, { transform: [style.transform, 'translateX(' + copyX + 'px)'], opacity: [parseFloat(style.opacity), 0] }, 0, .7);
        model.cards.forEach((card) => {
            segment(sequence, card.wrapper, { transform: [getComputedStyle(card.wrapper).transform, 'translate(' + card.x + 'px,' + card.y + 'px)'] }, 0, .7);
            [...card.shells, ...card.leaves].forEach((element) => segment(sequence, element, { opacity: [parseFloat(getComputedStyle(element).opacity), 0] }, 0, .7));
        });
        if (model.centre) {
            segment(sequence, model.centre, { opacity: [parseFloat(getComputedStyle(model.centre).opacity), 0] }, 0, .5, SETTLE);
        }
        if (model.media) {
            segment(sequence, model.media, { opacity: [parseFloat(getComputedStyle(model.media).opacity), 0], transform: [getComputedStyle(model.media).transform, 'translateY(120px)'] }, 0, .7);
        }
        return sequence;
    }

    function requestTick() {
        if (visible && !paused && !document.hidden && !frame) {
            frame = requestAnimationFrame(tick);
        }
    }

    function show(nextIndex, outgoing = null, firstAppearance = false) {
        index = nextIndex;
        phase = 'intro';
        holdTime = 0;
        slides.forEach((slide) => { slide.hidden = false; });
        updateUI();
        if (reduced.matches || typeof models[index].copy.animate !== 'function') {
            settle(models[index]);
            cleanup();
            phase = 'hold';
            requestTick();
            return;
        }
        leaving = outgoing;
        if (outgoing !== null) {
            slides[outgoing].classList.add(MOVING_CLASS);
            slides[index].classList.add(MOVING_CLASS);
        }
        const sequence = outgoing === null ? [] : exit(models[outgoing]);
        sequence.push(...entrance(models[index], firstAppearance));
        const startTime = document.timeline.currentTime;
        const animations = sequence.map(({ element, values, at, duration, easing }) => {
            const animation = element.animate(values, { delay: at, duration, easing, fill: 'forwards' });
            animation.startTime = startTime;
            return animation;
        });
        const controller = { animations, clock: animations[0] };
        timeline = controller;
        const token = generation;
        if (!visible || paused || document.hidden) {
            pauseTimeline();
        }
        Promise.all(animations.map((animation) => animation.finished)).then(() => {
            if (token !== generation || timeline !== controller) {
                return;
            }
            settle(models[index]);
            animations.forEach((animation) => animation.cancel());
            timeline = null;
            cleanup();
            phase = 'hold';
            holdTime = 0;
            requestTick();
        }).catch(() => {});
        requestTick();
    }

    function select(nextIndex, manual = false, restart = false) {
        if (manual) {
            autoplay = false;
            paused = false;
            resumable = false;
        }
        if (nextIndex === index && !restart) {
            if (!paused && !document.hidden) {
                resumeTimeline();
            }
            updateUI();
            return;
        }
        generation += 1;
        stopTimeline();
        cleanup();
        const outgoing = index;
        show(nextIndex, restart || reduced.matches ? null : outgoing, restart && !started);
        started = true;
    }

    function tick(now) {
        frame = 0;
        const delta = lastTime ? Math.min(now - lastTime, 100) : 0;
        lastTime = now;
        if (leaving !== null && timeline && timeline.clock.currentTime >= 700) {
            slides[leaving].classList.remove(MOVING_CLASS);
            slides[index].classList.remove(MOVING_CLASS);
            leaving = null;
        }
        if (visible && !paused && !document.hidden && phase === 'hold' && autoplay) {
            holdTime += delta;
            if (holdTime >= HOLD) {
                if (index === slides.length - 1) {
                    autoplay = false;
                    resumable = false;
                    holdTime = HOLD;
                    updatePlay();
                } else {
                    swiper ? swiper.slideTo(index + 1) : select(index + 1);
                }
            }
        }
        paintProgress();
        if (visible && !document.hidden && !paused && (timeline || autoplay)) {
            requestTick();
        }
    }

    resize();
    models.forEach(settle);
    cleanup();
    updateUI();
    swiper = startSlider(slider, {
        initialSlide: initialIndex, rewind: true, speed: 0, virtualTranslate: true,
        slidesPerView: 1, slidesPerGroup: 1, spaceBetween: 0,
        watchOverflow: false, grabCursor: true,
        keyboard: { enabled: true, onlyInViewport: true },
        on: {
            slideChange(currentSwiper) {
                if (!suppressSlideChange) {
                    select(currentSwiper.activeIndex);
                }
            },
            touchStart() {
                autoplay = false; paused = false; resumable = false;
                resumeTimeline();
                updatePlay(); paintProgress();
            },
            keyPress() {
                autoplay = false; paused = false; resumable = false;
                resumeTimeline();
                updatePlay(); paintProgress();
                requestTick();
            },
        },
    });
    play?.addEventListener('click', () => {
        if (autoplay && !paused) {
            paused = true;
            resumable = true;
            pauseTimeline();
        } else if (resumable) {
            paused = false; autoplay = true; resumable = false;
            resumeTimeline();
            lastTime = 0;
            requestTick();
        } else {
            autoplay = true; paused = false; resumable = false;
            suppressSlideChange = true;
            swiper?.slideTo(0, 0, false);
            suppressSlideChange = false;
            select(0, false, true);
            lastTime = 0;
            requestTick();
        }
        updatePlay();
        paintProgress();
    });

    function selectManual(nextIndex) {
        if (swiper && swiper.activeIndex !== nextIndex) {
            autoplay = false; paused = false; resumable = false;
            swiper.slideTo(nextIndex);
        } else {
            select(nextIndex, true);
        }
    }
    tabs.forEach((tab, tabIndex) => {
        tab.addEventListener('click', () => selectManual(tabIndex));
        tab.addEventListener('keydown', (event) => {
            const rtl = getComputedStyle(section).direction === 'rtl';
            let nextIndex;
            if (event.key === 'ArrowRight') {
                nextIndex = (index + (rtl ? slides.length - 1 : 1)) % slides.length;
            } else if (event.key === 'ArrowLeft') {
                nextIndex = (index + (rtl ? 1 : slides.length - 1)) % slides.length;
            } else if (event.key === 'Home') {
                nextIndex = 0;
            } else if (event.key === 'End') {
                nextIndex = slides.length - 1;
            }
            if (nextIndex !== undefined) {
                event.preventDefault();
                event.stopPropagation();
                tabs[nextIndex].focus({ preventScroll: true });
                selectManual(nextIndex);
            }
        });
    });
    document.addEventListener('visibilitychange', syncVisibility);
    reduced.addEventListener('change', () => {
        if (reduced.matches) {
            generation += 1;
            stopTimeline();
            settle(models[index]);
            cleanup();
            phase = 'hold';
            holdTime = 0;
            paintProgress();
        }
        if (stepsSwiper) {
            stepsSwiper.params.speed = reduced.matches ? 0 : 350;
        }
    });
    if ('ResizeObserver' in window) {
        let previousWidth = -1;
        new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;
            if (width !== previousWidth) {
                previousWidth = width;
                resize();
                scheduleStepsLayout();
            }
        }).observe(slider);
        if (steps) {
            let previousStepsWidth = -1;
            new ResizeObserver((entries) => {
                const width = entries[0].contentRect.width;
                if (width !== previousStepsWidth) {
                    previousStepsWidth = width;
                    scheduleStepsLayout();
                }
            }).observe(steps);
        }
    }
    stackedMedia.addEventListener('change', () => {
        resize();
        scheduleStepsLayout();
    });

    new MutationObserver(() => {
        const direction = getComputedStyle(section).direction;
        const rtl = direction === 'rtl';
        if (swiper?.rtl !== rtl) {
            swiper?.changeLanguageDirection?.(direction);
        }
        if (stepsSwiper) {
            if (stepsSwiper.rtl !== rtl) {
                destroyStepsSlider();
            }
        }
        resize();
        scheduleStepsLayout();
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] });

    function syncVisibility() {
        lastTime = 0;
        if (!visible || document.hidden) {
            pauseTimeline();
            cancelAnimationFrame(frame);
            frame = 0;
            return;
        }
        if (!started && section.classList.contains('animations-in-viewport--active')) {
            started = true;
            generation += 1;
            show(index, null, true);
        } else {
            resumeTimeline();
        }
    }

    const observer = new MutationObserver(() => {
        syncVisibility();
        if (started) {
            observer.disconnect();
        }
    });
    observer.observe(section, { attributes: true, attributeFilter: ['class'] });
    if ('IntersectionObserver' in window) {
        // Track the artwork itself: the section heading can enter much earlier.
        new IntersectionObserver((entries) => {
            const entry = entries[entries.length - 1];
            visible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
            syncVisibility();
        }, { threshold: [0, 0.1] }).observe(slider);
    } else {
        visible = true;
        syncVisibility();
    }
};

export const initSliderHomePagePlatformControl = () => {
    document.querySelectorAll('[data-platform-control-slider]').forEach(initPlatformControlSlider);
};
